import { type Eligibility } from "./newsletter.models"
import { i18n } from "../internationalize/i18n.service"
import { validateEmail } from "../core/helpers"

import './newsletter.scss'
import { AuthService } from "../auth/services/auth.service"
import { type NewsletterService } from "./newsletter.service"
import { FeedbackService } from "./userMessage.service"

import gbFlag from "../assets/flags/gb-flag.svg?raw"
import frFlag from "../assets/flags/fr-flag.svg?raw"

export class NewsletterManager extends HTMLElement {
    private frenchNlId: string = this.getAttribute('data-french-id')!
    private englishNlId: string = this.getAttribute('data-english-id')!
    private feedbackService!: FeedbackService;

    private authService: AuthService = window.tbwServices.authService;
    private eligibility!: Eligibility[];

    private languageSelector!: HTMLDivElement
    private languageSummaryDiv!: HTMLDivElement
    private emailContainer!: HTMLInputElement
    public formContainer!: HTMLFormElement

    constructor(readonly newsletterService: NewsletterService = window.tbwServices.newsletterService) {
        super()
        this.innerHTML = this.render()

        // Scope DOM lookups to this component instance
        this.emailContainer = this.querySelector(`.js-form-email`)!;
        this.formContainer = this.querySelector(`.js-newsletter-form`)!;
    }

    async connectedCallback() {
        if (!this.frenchNlId || !this.englishNlId) { throw new Error("Newsletter ids not found") }
        if (!this.authService.isAuthenticated) { return }
        if (!this.emailContainer || !this.formContainer) { throw new Error("Missing containers") }
        if (!gbFlag || !frFlag) { throw new Error("Missing flags") }

        this.feedbackService = new FeedbackService(this);

        await this.init()
    }

    async init() {
        await this.checkEligibility();

        // Check if user is subscribed to at least one newsletter
        const isSubscribedToAny = this.eligibility.some(e => e.canUnsubscribe === true);

        if (isSubscribedToAny) {
            this.showUnsubscribeUI();
        } else {
            this.showSubscribeUI();
        }
    }

    private showSubscribeUI(): void {
        const subscribeButton = this.createSubscribeButton();
        this.languageSelector = this.createLanguageSelector();

        this.formContainer.appendChild(this.languageSelector);
        this.formContainer.appendChild(subscribeButton);
    }

    private showUnsubscribeUI(): void {
        // Show unsubscribe options for each newsletter the user is subscribed to
        const summaryContainer = document.createElement("div")
        summaryContainer.className = "summary-container"

        this.eligibility
            .filter((e) => e.canUnsubscribe === true)
            .forEach((eligibility) => {

                this.languageSummaryDiv = this.createLanguageSummary(eligibility.newsletter.language);
                this.languageSummaryDiv.appendChild(this.createUnsubscribeButton(eligibility.newsletter.listId))
                this.languageSummaryDiv.className = "item"

                summaryContainer.appendChild(this.languageSummaryDiv);

            });
        this.formContainer.appendChild(summaryContainer)
    }

    render() {
        const defaultEmail = window.tbwServices.authService.profile.userEmail
        return `
            <form class="newsletter-form_div js-newsletter-form">
                <input type="email" value=${defaultEmail} style='cursor: not-allowed' disabled=true class="text-input is-full-width js-form-email" name="email" placeholder="Email" />
            </form>
        `
    }

    createLanguageSelector(): HTMLDivElement {
        const defaultLanguage = i18n.getLanguage() === "fr" ? 'french' : 'english'
        const defaultFlag = defaultLanguage === "french" ? frFlag : gbFlag

        const container = document.createElement("div")
        container.className = "nl-language-selector-container"

        const displayButton = document.createElement("button")
        displayButton.type = "button"
        displayButton.className = "nl-language-selector-button"
        displayButton.innerHTML = `${defaultFlag}`

        const dropdown = document.createElement("div")
        dropdown.className = "nl-language-dropdown"
        dropdown.style.display = "none"

        const englishOption = document.createElement("div")
        englishOption.className = `nl-language-option ${defaultLanguage === "english" ? "selected" : ""}`
        englishOption.innerHTML = `${gbFlag}`
        englishOption.dataset.value = "english"

        const frenchOption = document.createElement("div")
        frenchOption.className = `nl-language-option ${defaultLanguage === "french" ? "selected" : ""}`
        frenchOption.innerHTML = `${frFlag}`
        frenchOption.dataset.value = "french"

        dropdown.appendChild(englishOption)
        dropdown.appendChild(frenchOption)

        // Store current value
        container.dataset.value = defaultLanguage

        // Toggle dropdown
        displayButton.addEventListener("click", () => {
            const isOpen = dropdown.style.display === "block"
            dropdown.style.display = isOpen ? "none" : "block"
        })

        // Handle option selection
        const selectOption = (option: HTMLDivElement) => {
            // Update display
            displayButton.innerHTML = option.innerHTML
            container.dataset.value = option.dataset.value!

            // Update selection
            dropdown.querySelectorAll('.nl-language-option').forEach(opt => opt.classList.remove('selected'))
            option.classList.add('selected')

            // Close dropdown
            dropdown.style.display = "none"
        }

        englishOption.addEventListener("click", () => selectOption(englishOption))
        frenchOption.addEventListener("click", () => selectOption(frenchOption))

        // Close dropdown when clicking outside
        document.addEventListener("click", (e) => {
            if (!container.contains(e.target as Node)) {
                dropdown.style.display = "none"
            }
        })

        container.appendChild(displayButton)
        container.appendChild(dropdown)

        return container
    }

    createSubscribeButton(): HTMLButtonElement {
        const subscribeButton = document.createElement("button")
        subscribeButton.className = "button"
        subscribeButton.type = "submit"
        subscribeButton.textContent = "S'abonner"
        subscribeButton.addEventListener("click", (event) => {
            event.preventDefault();
            this.handleSubscribe(subscribeButton);
        });
        return subscribeButton
    }

    createUnsubscribeButton(listId: string): HTMLButtonElement {
        const unsubscribeButton = document.createElement("button")
        unsubscribeButton.className = "button"
        unsubscribeButton.textContent = "Se désabonner"
        unsubscribeButton.addEventListener("click", (event) => {
            event.preventDefault();
            this.handleUnsubscribe(unsubscribeButton, listId);
        });
        return unsubscribeButton
    }


    createLanguageSummary(language: string): HTMLDivElement {
        const summaryLanguage = document.createElement("div")
        summaryLanguage.innerHTML = `
        <div class="nl-language-selector">
            ${language.toLocaleLowerCase().includes("fr") ? frFlag : gbFlag}
        </div>
        `
        return summaryLanguage
    }

    checkEligibility = async (): Promise<void> => {
        const frenchEligibility: Eligibility = await this.newsletterService.checkEligibility(this.frenchNlId);
        const englishEligibility: Eligibility = await this.newsletterService.checkEligibility(this.englishNlId);
        this.eligibility = [frenchEligibility, englishEligibility];
    }

    private async handleUnsubscribe(unsubscribeButton: HTMLButtonElement, id: string): Promise<void> {
        this.feedbackService.hideMessages();
        this.feedbackService.setSubmitButtonLoading(unsubscribeButton, true);

        try {
            await this.newsletterService.unsubscribe(id);

            const successMessage = i18n.getLanguage() === "fr"
                ? "Vous êtes désabonné de la newsletter"
                : "You are unsubscribed from the newsletter";
            this.feedbackService.showSuccessMessage(successMessage);
            this.feedbackService.reloadPageWithTimeout();
            window.tbwServices.analyticsService.track("newsletter_unsubscribe", { newsletter_id: id });
        }
        catch (error: any) {
            const errorMessage = i18n.getLanguage() === "fr"
                ? "Erreur lors du désabonnement"
                : "Error during unsubscription";
            this.feedbackService.showErrorMessage(errorMessage);
        }
        finally {
            this.feedbackService.setSubmitButtonLoading(unsubscribeButton, false);
        }
    }

    private async handleSubscribe(subscribeButton: HTMLButtonElement): Promise<void> {
        this.feedbackService.hideMessages();
        this.feedbackService.setSubmitButtonLoading(subscribeButton, true);

        try {
            if (validateEmail(this.emailContainer.value) === false) {
                const errorMessage = i18n.getLanguage() === "fr" ? "Veuillez entrer une adresse email valide" : "Please enter a valid email address";
                throw new Error(errorMessage);
            }

            const formEmail = this.emailContainer.value;
            const formLanguage = this.languageSelector.dataset.value!;
            const id = formLanguage === "french" ? this.frenchNlId : this.englishNlId;

            const response = await this.newsletterService.subscribe(formEmail, id);
            if (response instanceof Error) {
                const errorMessage = i18n.getLanguage() === "fr" ? "Une erreur est survenue lors de l'abonnement à la newsletter" : "An error occurred while subscribing to the newsletter";
                throw new Error(errorMessage);
            }

            const successMessage = i18n.getLanguage() === "fr" ? "Succès ! Vous êtes abonné à la newsletter" : "Success ! You are subscribed to the newsletter";
            window.tbwServices.analyticsService.track("newsletter_signup", { newsletter_id: id });
            this.feedbackService.showSuccessMessage(successMessage);
            this.feedbackService.reloadPageWithTimeout();

            return response
        }
        catch (error: any) {
            this.feedbackService.showErrorMessage(error.message);
        }
        finally {
            this.feedbackService.setSubmitButtonLoading(subscribeButton, false);
        }
    }
}

